import { useTranslation } from "@vixi/localization";
import _ from "lodash";
import { ComponentProps, CSSProperties, forwardRef, useEffect, useMemo, useRef } from "react";
import CourseTabItem from "../TabItem/TabItem";

import * as Tabs from "@radix-ui/react-tabs";
import {
  CourseTabComponentProps,
  CourseTabGroup,
  hasContent,
  hasSubtabs,
  STEP_TYPE,
} from "@vixi/types";
import { course_structures } from "@vixi/utils";
import { CourseStepContent } from "../CourseStepContent/CourseStepContent";
import { AddStepMenu } from "./AddStepMenu";
import { CourseTabList, CourseTabsNav } from "./CourseTabsNav";
import { CourseTabTitle } from "./CourseTabTitle";
import { cn } from "@/lib/utils";
import { courseTabsClasses } from "@/classes/courseTabsClasses";
import { useCourseEditorContext } from "../context";
import { Button } from "@/components/base/button";

// Constants
const MAX_DEPTH = 4;

// types
interface TabPanelProps extends Tabs.TabsContentProps {
  flex?: number;
  minWidth?: CSSProperties["minWidth"];
}

// Styling
const TabsRoot = forwardRef<HTMLDivElement, { isSteps?: boolean; isRtl?: boolean } & Tabs.TabsProps>(
  ({ isSteps, isRtl, ...props }, ref) => (
    <Tabs.Root
      ref={ref}
      {...props}
      orientation="vertical"
      className={cn(
        courseTabsClasses.root,
        "flex h-full flex-1 items-center bg-inherit",
        isSteps ? "gap-5" : "gap-2.75",
        isRtl && "flex-row-reverse",
      )}
    />
  ),
);

const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(({ flex, minWidth, ...props }, ref) => (
  <Tabs.Content
    ref={ref}
    {...props}
    className={cn(
      "flex h-full flex-1 items-center bg-inherit data-[state=inactive]:hidden! data-[state=inactive]:flex-0!",
      props.className,
    )}
    style={{ flex, minWidth }}
  />
));

const CourseTabContent = forwardRef<HTMLDivElement, ComponentProps<"div">>((props, ref) => {
  const { isRtl } = useTranslation();
  return (
    <div
      ref={ref}
      {...props}
      className={cn(
        courseTabsClasses.content,
        "bg-paper my-auto box-border flex h-full min-w-77.5 flex-1 flex-col rounded py-4",
        isRtl ? "me-4" : "ms-4",
        props.className,
      )}
    />
  );
});

// Main component
interface CourseTabsProps extends Partial<CourseTabComponentProps> {
  tabList: CourseTabGroup;
  depth?: number;
  showTabNumbers?: boolean;
}

/**
 * @param props.path - root: ["chapters"] or undefined, chapter: ["chapters", index], lesson: ["chapters", index, "lessons", index]
 */
export function CourseTabs({
  tabList,
  showTabNumbers,
  depth = 0,
  path: propsPath,
}: CourseTabsProps) {
  const { t, tDeep, byLocale, isRtl } = useTranslation();

  const { addItem, activeTabs, setActiveTabs, mode } = useCourseEditorContext();
  const tabListRef = useRef<HTMLDivElement | null>(null);

  const [selectedTab, setSelectedTab] = useMemo(() => {
    const activeTab = activeTabs[depth] ?? 1;
    const setSelectedTab = (newValue: number) => {
      setActiveTabs((prev) => {
        prev[depth] = newValue;
        return [...prev];
      });
    };

    return [activeTab, setSelectedTab] as const;
  }, [activeTabs, setActiveTabs]);

  useEffect(() => {
    setSelectedTab(1);
  }, [depth]);

  const level = tabList.level;
  const isSteps = level === "steps";

  /**
   * @param path - root: ["chapters"], chapter: ["chapters", index, "lessons"], lesson: ["chapters", index, "lessons", index, "steps"]
   */
  const path = useMemo(() => {
    if (propsPath) {
      return [...propsPath, level];
    }

    return [level];
  }, [propsPath, level]);

  // Determine tooltip image based on level and current step type
  const tooltipConfig = useMemo(() => {
    if (level === "chapters") {
      return { tooltipImage: "/images/screens/chapterscreen.png" };
    }
    if (level === "lessons") {
      return { tooltipImage: "/images/screens/lessonscreen.png" };
    }
    if (level === "steps") {
      // Check if current selected tab is a conversation step
      const currentTab = tabList.tabs?.[selectedTab - 1];
      if (currentTab && hasContent(currentTab) && currentTab.content?.type === STEP_TYPE.CONVERSATION) {
        return { tooltipImages: ["/images/screens/convscreen1.png", "/images/screens/convscreen2.png"] };
      }
      // For other step types, we could show a different image or nothing
      return null;
    }
    return null;
  }, [level, tabList.tabs, selectedTab]);

  const handleChange = (newValue: string | number | null) => {
    if (typeof newValue === "number") setSelectedTab(newValue);
  };

  const handleAddItem = (item?: unknown, suffixPath?: (string | number)[]) => {
    const addPath = suffixPath ? [...path, ...suffixPath] : path;
    const nextId = Math.max(...tabList.tabs?.map((t) => t.id)) + 1;

    const newItem = tDeep(item ?? course_structures[level].genFn?.(nextId));

    if (newItem) {
      addItem(addPath, newItem);
    }

    _.delay(() => {
      tabListRef.current?.scrollTo({
        top: tabListRef.current.scrollHeight,
        behavior: "smooth",
      });

      setSelectedTab(tabList.tabs.length + 1);
    }, 100);
  };

  return (
    <div
      style={{ flex: MAX_DEPTH - depth }}
      className={cn(
        `${courseTabsClasses.root} ${courseTabsClasses.depth(depth)}`,
        "flex h-full",
        isRtl && "flex-row-reverse",
      )}
    >
      <TabsRoot
        value={selectedTab.toString()}
        onValueChange={(v) => handleChange(parseInt(v))}
        defaultValue={"1"}
        isSteps={isSteps}
        isRtl={isRtl}
      >
        {/* TabsNav Container */}
        <div className="box-border h-full flex-1" style={{ paddingBlock: (depth ?? 0) * 20 }}>
          <CourseTabsNav>
            <CourseTabTitle
              tooltipImage={tooltipConfig?.tooltipImage}
              tooltipImages={tooltipConfig?.tooltipImages}
            >
              {t(course_structures[level].listLabel)}
            </CourseTabTitle>

            {/* TabList */}
            <CourseTabList
              ref={tabListRef}
              className="CourseTabList"
              variant={!isSteps ? "buttonList" : "orderedList"}
            >
              {/* Tab Button */}
              {tabList.tabs?.map((tab, index, tabs) => {
                const buttonVariant = hasSubtabs(tab) ? "outlined" : "text";
                const tabNumber =
                  tab.subIndex === undefined
                    ? tab.index
                    : byLocale({
                        fallback: `${tab.index}.${tab.subIndex}`,
                        ar: tab.subIndex === 1 ? tab.index : tab.subIndex - 1,
                      });
                const tabPath = [...path, index];

                return (
                  <CourseTabItem
                    key={tab.id}
                    path={tabPath}
                    // index={tab.index}
                    value={`${index + 1}`}
                    tabNumber={tabNumber}
                    variant={buttonVariant}
                    showNumber={showTabNumbers || isSteps}
                    disableDelete={tabs.length === 1}
                    isSelected={selectedTab === index + 1}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    singleTitle={!!tab.title?.length}
                    text={
                      tab.title ??
                      ((hasSubtabs(tab) && tab.titles?.[tab.selectedTitle ?? 0]) ||
                        `${t(course_structures[level].label)} ${tab.index + 1}`)
                    }
                  />
                );
              })}
            </CourseTabList>

            {/* Add Item Button */}
            {mode === "edit" &&
              (!isSteps ? (
                <Button
                  className={cn(courseTabsClasses.addItemButton, "self-center")}
                  variant="outlined"
                  size="pill"
                  onClick={() => handleAddItem()}
                >
                  {t(course_structures[level].addLabel)}
                </Button>
              ) : (
                <AddStepMenu tabList={tabList} addItem={handleAddItem} />
              ))}
          </CourseTabsNav>
        </div>

        {/* Tab Panel(s) */}
        {tabList.tabs?.map((tab, index) => (
          <TabPanel
            key={tab.id}
            flex={hasSubtabs(tab) ? MAX_DEPTH - depth - 1 : 2}
            minWidth={hasSubtabs(tab) ? "min-content" : "auto"}
            value={(index + 1).toString()}
          >
            {hasSubtabs(tab) ? (
              /* Render subtabs if present */
              <CourseTabs tabList={tab.tabList} depth={depth + 1} path={path?.concat(index)} />
            ) : (
              /* Render content if it has content*/
              hasContent(tab) && (
                <CourseTabContent
                  className={courseTabsClasses.contentType(
                    tab.content.type === STEP_TYPE.CONVERSATION ? "conversation" : "question",
                  )}
                  key={tab.id}
                >
                  <CourseStepContent content={tab.content} id={tab.id} path={path?.concat(index)} />
                </CourseTabContent>
              )
            )}
          </TabPanel>
        ))}
      </TabsRoot>
    </div>
  );
}

export default CourseTabs;
